import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren, ViewContainerRef
} from '@angular/core';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {select, Store} from '@ngrx/store';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {
  selectActiveWorkflow,
  selectRequestedRevision,
  selectTestElapsedTime
} from '@ispm/workflow/store/workflow-config.selectors';
import {filter, map, merge, startWith, takeUntil, tap, withLatestFrom} from 'rxjs/operators';
import {selectUiConfiguration} from '../../../../configuration/store/configuration.selector';
import {get, has} from 'lodash';
import {WorkflowBarItem} from '@ispm/shared/store/shared.reducer';
import {WorkflowConfigService} from '@ispm/workflow/services/workflow-config.service';
import {IWorkflow} from '@ispm/core/interfaces/workflow.interface';
import {IRevision} from '@ispm/test/interfaces/diagnostic-order.interface';
import {WorkflowActiveTabConfig} from '@ispm/workflow/interfaces/workflow-ui-config.interface';
import { TestService } from '../../services/test.service';
import {ActivatedRoute, Router} from '@angular/router';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";
import {testViewWithPatientProfile, testViewPathologistPath} from "@ispm/test/routes/test-view-state-names";

@Component({
  selector: 'ispm-curation-workflow-stepper',
  templateUrl: './curation-workflow-stepper.component.html',
  styleUrls: ['./curation-workflow-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurationWorkflowStepperComponent implements OnInit, OnDestroy {

  @ViewChildren('stepHeader', {read: ViewContainerRef}) stepHeaders: QueryList<ElementRef>;

  public steps$: Observable<Array<any>>;
  public activeIndex: number;
  public revision;
  public testElapsedTime;
  public isPerformer = false;

  @Input() currentWorkflow: IWorkflow;
  @Input() currentRevision: IRevision;
  @Input() isPatientContext: boolean;

  @Output()
  public onNotSupported: EventEmitter<boolean> = new EventEmitter<boolean>();

  private readonly itemsToShow = ['Select pathway', 'Select treatment', 'Signoff', 'Canceled', 'Stopped'];
  private readonly selectPathwayStepName = this.itemsToShow[0];
  private readonly selectTreatmentStepName = this.itemsToShow[1];
  private _alive = true;
  private destroy$ = new Subject();
  private tabChanged$: Subject<string> = new Subject();
  private selectedTab: string;
  private originalStateName: string;
  private numOfItems = 0;

  private currentItemSubject = new ReplaySubject<{ index: number }>(1);
  public currentItem$ = this.currentItemSubject.pipe(
    takeUntil(this.destroy$),
    startWith({index: 0}));

  public tabsConfig$: Observable<WorkflowActiveTabConfig[]>;
  public tabsTitles$: Observable<string[]>;

  readonly stepToTabAdapter = {
    'Select treatment': 'Therapies',
    'Select pathway': 'Pathway',
    'Signoff': 'Consent form & report',
    'Canceled': ''
  };

  constructor(private _changeDetector: ChangeDetectorRef,
              private store: Store<IgenomicsState>,
              private testService : TestService,
              private workflowConfigService: WorkflowConfigService,
              private router: Router,
              private routerManagerService: RouterManagerService,
              private activatedRoute: ActivatedRoute) {
  }

  ngOnInit() {
    this.steps$ = this.getSteps$();

    if (this.isPatientContext) {
      this.initWithPatientContext();
    } else {
      this.initWithOrderContext();
    }

    this.initTabsConfig();
    this.subscribeToTabsConfig();
  }

  public isStepHasConfirmButton(stepItem): boolean {
    return stepItem.isActive
      && stepItem.originalName != this.selectTreatmentStepName
      && (stepItem.originalName != this.selectPathwayStepName || !this.getHideTreatmentSelectionButton());
  }

  public stepClicked(tabName, index: number) {
    if (!tabName) {
      return;
    }
    this.markStepAsBold(index);
    this.onTabChanged(tabName);
  }

  private getHideTreatmentSelectionButton(): boolean {
    return this.testService.testConfiguration?.isSelectPathwayBarEnabled;
  }

  private markStepAsBold(index: number) {
    if (index < 0) {
      return;
    }
    if (index >= this.numOfItems) {
      this.currentItemSubject.next({index: this.numOfItems - 1});
      return;
    }
    this.currentItemSubject.next({index});
  }

  private initWithPatientContext() {
    this.originalStateName = testViewWithPatientProfile;
  }

  private initWithOrderContext() {
    this.originalStateName = testViewPathologistPath;
  }

  private getSteps$() {
    return this.store.pipe(
      select(selectActiveWorkflow),
      merge(
        this.store.pipe(select('shared'))
      ),
      withLatestFrom(
        this.store.pipe<any>(select('shared')),
        this.store.pipe<any>(select(selectTestElapsedTime)),
        this.store.pipe(select(selectUiConfiguration('workflowVisibility.barItems', 'Test'))),
        this.store.pipe(select(selectRequestedRevision))
      ),
      map(([streamSource, workflowBarOverride, testElapsedTime, items, revision]) => {
        const emptyState = {items: null, status: null};

        this.revision = revision;
        this.testElapsedTime = parseInt(testElapsedTime, 10);
        if (!(revision && items)) {
          return emptyState;
        } else if (!has(revision, 'ui.workflowVisibility.barItems')) {
          this.emitForNotSupported(true);
          return emptyState;
        } else if (workflowBarOverride.workflow_bar) {
          // in case of state override by ui actions (mostly used by revise for preview)
          return {
            items: workflowBarOverride.workflow_bar,
            status: workflowBarOverride.status
          };
        } else {
          return {
            items: this.formatItems(items, revision),
            status: revision.status
          };
        }
      })
    ).pipe( // new pipe so compiler can follow type signature
      filter(({items}: { items: any[]; status: string }) => !!items),
      map(({items, status}) => {
        const relevantItems = items.filter(item => this.itemsToShow.includes(item.name));
        return {items: relevantItems, status};
      }),
      tap(() => this.emitForNotSupported(false)),
      map(({items, status}) => {
        if (items && items.length > 0) {
          this.numOfItems = items.length;
          items[0].pos = 'first';
          items[items.length - 1].pos = 'last';
          this.activeIndex = items.findIndex((e) => e.isActive);
          if (!!status && this.activeIndex !== -1 && items[this.activeIndex].fromStatus) {
            items[this.activeIndex].name = status;
          }
          if (status === 'Navigation Complete') {
            this.activeIndex = items.length; // if navigation is complete, all steps should be active
          }
          this.currentItemSubject.next({index: this.activeIndex});
        }
        return items;
      }),
      tap(() => {
        if (this._alive) {
          this._changeDetector.markForCheck();
          this._changeDetector.detectChanges();
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private subscribeToTabsConfig() {
    this.tabsConfig$.subscribe(tabConfig => {
      let activeTab = tabConfig.find(tab => tab.isActive === true);
      if (get(this.routerManagerService.parameters, 'payload')) {
        activeTab = tabConfig.find(tab => get(tab, 'payload') === get(this.routerManagerService.parameters, 'payload'));
      }
      if (activeTab) {
        this.onTabChanged(activeTab.title);
      } else {
        const currentStateName = this.routerManagerService.currentState;
        const selectedSubState = currentStateName.substr(this.originalStateName.length + 1);
        const selectedConfig = selectedSubState ? tabConfig.find(tab => tab.type === selectedSubState) : null;
        if (selectedConfig) {
          this.selectedTab = selectedConfig.title;
        } else {
          this.onTabChanged(tabConfig[0].title);
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._changeDetector.detach();
    this._alive = false;
  }

  public trackByFn(item, index) {
    return index;
  }

  private initTabsConfig() {
    this.tabsConfig$ = this.workflowConfigService.getUiConfig().pipe(
      takeUntil(this.destroy$.asObservable()),
      takeUntil(this.workflowConfigService.getActiveWorkflow(false).pipe(filter(wf => !wf))), // listen until workflow cleared
      map(config => config.activeTabs)
    );
    this.tabsTitles$ = this.tabsConfig$.map(configs => configs.map(config => config.title));
    this.tabChanged$.pipe(withLatestFrom(this.tabsConfig$)).subscribe(([tabName, tabConfig]) => {
      const currentTab = tabConfig.find(tab => tab.title === tabName);
      if (!!currentTab) {
        const tabType = currentTab.type;
        if (this.selectedTab !== tabName) {
          this.selectedTab = tabName;
          const payload = get(currentTab, 'payload');
          if (this.routerManagerService.currentState !== tabType) {
            this.router.navigate([tabType], {relativeTo: this.activatedRoute});
          }
          if ((payload) && (this.routerManagerService.parameters.payload!== payload)) {
            this.router.navigate([tabType], {queryParams: payload, relativeTo: this.activatedRoute});
          }
        }
      }
    });
  }

  onTabChanged($event: string) {
    this.tabChanged$.next($event);
  }

  private formatItems(uiItems: WorkflowBarItem[], revision, keepItem = false) {
    if (!uiItems) {
      return null;
    }
    return uiItems.map(item => Object.assign({originalName: item.name}, item)).filter((uiItem) => {
      if (uiItem.isActive) {
        if (revision.humanReadableMessage) {
          uiItem.tooltip = revision.humanReadableMessage;
        }
        if (['canceled', 'failed', 'cancelled', 'stopped'].includes(revision.status.toLowerCase())) {
          uiItem.name = revision.status;
          uiItem.fromStatus = true;
        }
      }
      if (uiItem.conditionPrevStepDone) {
        for (const revItem of revision.items) {
          if (revItem.name === uiItem.conditionPrevStepDone) {
            if (revItem.status.toLowerCase() !== 'done') {
              return false;
            }
            if (!keepItem) {
              delete uiItem.conditionPrevStepDone;
            }
            break;
          }
        }
      }
      return true;
    });
  }

  private emitForNotSupported(status: boolean) {
    this.onNotSupported.emit(status);
  }

  public isNeedKeepPreviousPath() {
    return !!this.currentRevision && this.currentRevision.status !== 'Pathway selection';
  }


}
